import styled, { DefaultTheme } from "styled-components";
import { Card, Typography, Button, Space, Modal, Tag } from "antd";

const { Title, Text } = Typography;

declare module "styled-components" {
  export interface DefaultTheme {
    mode?: "light" | "dark";
  }
}

const getThemeValue = <T,>(props: { theme?: DefaultTheme }, darkValue: T, lightValue: T): T => {
  return props.theme?.mode === "dark" ? darkValue : lightValue;
};

export const MainContainer = styled.div`
  padding: 32px;
  color: ${(props) => getThemeValue(props, "#ffffff", "#1A202C")} !important;
  background: ${(props) => getThemeValue(props, "#1A202C", "#F7FAFC")} !important;
  min-height: 100vh;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
  position: relative;

  .ant-typography {
    color: ${(props) => getThemeValue(props, "#ffffff", "#1A202C")} !important;
  }

  @media (max-width: 768px) {
    padding: 20px 16px;
  }
`;

export const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 32px;
  gap: 20px;
  padding: 24px;
  background: ${(props) => getThemeValue(props, "#2D3748", "#ffffff")} !important;
  border-radius: 16px;
  box-shadow: ${(props) => getThemeValue(
    props,
    "0 4px 20px rgba(0, 0, 0, 0.3), 0 1px 3px rgba(0, 0, 0, 0.2)",
    "0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)"
  )};
  border: ${(props) => getThemeValue(props, "1px solid #4A5568", "1px solid #E2E8F0")};

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const BackButton = styled(Button)`
  box-shadow: ${(props) => getThemeValue(
    props,
    "0 4px 12px rgba(0, 0, 0, 0.3)",
    "0 2px 8px rgba(0, 0, 0, 0.1)"
  )};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 50%;
  
  &:hover {
    transform: translateX(-1px);
    box-shadow: ${(props) => getThemeValue(
      props,
      "0 6px 16px rgba(0, 0, 0, 0.4)",
      "0 4px 12px rgba(0, 0, 0, 0.15)"
    )};
  }
`;

export const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const PageTitle = styled(Title)`
  margin: 0 !important;
  font-weight: 700 !important;
  letter-spacing: -0.5px;
`;

export const PageSubtitle = styled(Text)`
  color: ${(props) => getThemeValue(props, "#CBD5E0", "#718096")} !important;
`;

export const QuickStats = styled.div`
  display: flex;
  gap: 24px;
  align-items: center;

  @media (max-width: 768px) {
    display: none;
  }
`;

export const QuickStatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

export const QuickStatLabel = styled(Text)`
  font-size: 12px;
  color: ${(props) => getThemeValue(props, "#CBD5E0", "#718096")} !important;
`;

export const QuickStatValue = styled(Text)`
  font-size: 18px;
  font-weight: 700;
`;

export const QuickStatValueResolved = styled(QuickStatValue)`
  color: #52c41a !important;
`;

export const QuickStatValuePending = styled(QuickStatValue)`
  color: #ff4d4f !important;
`;

export const HeaderIcon = styled.div<{ iconColor: string }>`
  font-size: 28px;
  color: ${(props) => props.iconColor};
`;

export const OverviewSection = styled.div`
  margin-bottom: 32px;
`;

export const SectionTitle = styled(Title)`
  margin-bottom: 16px !important;
  display: block;

  @media (min-width: 768px) {
    display: none;
  }
`;

export const InfoTilesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

export const InfoTileCard = styled(Card)`
  background: ${(props) => getThemeValue(props, "#2D3748", "#ffffff")} !important;
  border: ${(props) => getThemeValue(props, "1px solid #4A5568", "1px solid #E2E8F0")};
  border-radius: 16px;
  height: 320px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${(props) => getThemeValue(
    props,
    "0 4px 20px rgba(0, 0, 0, 0.3), 0 1px 3px rgba(0, 0, 0, 0.2)",
    "0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)"
  )};
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #1890ff 0%, #722ed1 50%, #fa8c16 100%);
    opacity: 0.8;
    z-index: 1;
  }

  &:hover {
    box-shadow: ${(props) => getThemeValue(
      props,
      "0 8px 30px rgba(0, 0, 0, 0.4), 0 2px 6px rgba(0, 0, 0, 0.3)",
      "0 8px 30px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.08)"
    )};
    transform: translateY(-1px);
  }

  .ant-card-body {
    padding: 24px;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  @media (max-width: 768px) {
    height: 280px;
  }
`;

export const InfoTileHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
`;

export const InfoTileIcon = styled.div<{ iconColor: string }>`
  color: ${(props) => props.iconColor};
  font-size: 24px;
  flex-shrink: 0;
`;

export const InfoTileContent = styled.div`
  flex: 1;
`;

export const InfoTileTitle = styled(Text)`
  font-size: 18px;
  font-weight: 700;
  display: block;
  margin-bottom: 4px;
`;

export const InfoTileDescription = styled(Text)`
  font-size: 12px;
  color: ${(props) => getThemeValue(props, "#CBD5E0", "#718096")} !important;
  display: block;
`;

export const ChartContainer = styled.div`
  position: relative;
  width: 100%;
  height: 120px;
  margin: 16px 0;

  @media (max-width: 768px) {
    height: 100px;
  }
`;

export const ChartValue = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  pointer-events: none;
`;

export const ChartValueText = styled(Text)`
  font-size: 24px;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

export const PercentageContainer = styled.div`
  display: flex;
  justify-content: space-around;
  padding: 0 16px;
  margin-top: 16px;
`;

export const PercentageItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

export const PercentageLabel = styled(Text)`
  font-size: 12px;
  color: ${(props) => getThemeValue(props, "#CBD5E0", "#718096")} !important;
`;

export const PercentageValue = styled(Text)`
  font-size: 16px;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

export const CarouselSection = styled.div`
  margin-bottom: 32px;
`;

export const CarouselHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`;

export const CarouselTitle = styled(Title)`
  margin: 0 !important;
`;

export const CarouselSubtitle = styled(Text)`
  color: ${(props) => getThemeValue(props, "#CBD5E0", "#718096")} !important;
  font-size: 12px;
`;

export const CarouselWrapper = styled.div`
  width: 100%;
  overflow: visible;
  position: relative;
  padding: 0 64px;

  @media (max-width: 768px) {
    padding: 0;
  }

  .swiper {
    padding: 0;
    overflow: visible;
  }

  .swiper-slide {
    height: auto;
    width: auto !important;
  }

  .swiper-button-next,
  .swiper-button-prev {
    color: #3182CE;
    
    &::after {
      font-size: 20px;
    }
  }

  .swiper-pagination-bullet {
    background-color: #3182CE;
  }

  .swiper-pagination-bullet-active {
    background-color: #2B6CB0;
  }
`;

export const NavigationButtons = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  pointer-events: none;
  z-index: 2;
  padding: 0 16px;
  display: flex;
  justify-content: space-between;
`;

export const NavButton = styled.div`
  background: white;
  border-radius: 50%;
  padding: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  pointer-events: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  min-height: 40px;
  border: 1px solid #e2e8f0;
  transition: all 0.2s;
  position: relative;

  &:hover {
    background: #f7fafc;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

export const NavButtonPrev = styled(NavButton)`
  left: -20px;
`;

export const NavButtonNext = styled(NavButton)`
  right: -20px;
`;

export const NavIcon = styled.div`
  font-size: 16px;
  color: #4A5568;
`;

export const DisputeCard = styled(Card)`
  background: ${(props) => getThemeValue(props, "#2D3748", "#ffffff")} !important;
  border: ${(props) => getThemeValue(props, "1px solid #4A5568", "1px solid #E2E8F0")};
  border-radius: 16px;
  width: 340px;
  height: 320px;
  box-shadow: ${(props) => getThemeValue(
    props,
    "0 2px 8px rgba(0, 0, 0, 0.2)",
    "0 2px 8px rgba(0, 0, 0, 0.06)"
  )};
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #1890ff 0%, #722ed1 50%, #fa8c16 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${(props) => getThemeValue(
      props,
      "0 12px 32px rgba(0, 0, 0, 0.4), 0 4px 8px rgba(0, 0, 0, 0.3)",
      "0 12px 32px rgba(0, 0, 0, 0.15), 0 4px 8px rgba(0, 0, 0, 0.08)"
    )};
    border-color: ${(props) => getThemeValue(props, "#4A5568", "rgba(24, 144, 255, 0.3)")};

    &::before {
      opacity: 1;
    }
  }

  .ant-card-body {
    padding: 24px;
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  @media (max-width: 768px) {
    width: 280px;
    height: 260px;
  }

  @media (min-width: 480px) and (max-width: 768px) {
    width: 300px;
    height: 300px;
  }
`;

export const DisputeCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
`;

export const DisputeCardHeaderContent = styled(Space)`
  flex: 1;
  min-width: 0;
`;

export const DisputeCardContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const DisputeTitle = styled(Text)`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
  display: block;
`;

export const DisputeDescription = styled(Text)`
  font-size: 14px;
  color: ${(props) => getThemeValue(props, "#CBD5E0", "#718096")} !important;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
`;

export const DisputeCardFooter = styled.div`
  margin-top: auto;
`;

export const StatusTag = styled(Tag)<{ status: "Resolved" | "Pending" }>`
  background: ${(props) => (props.status === "Resolved" ? "#38A169" : "#E53E3E")} !important;
  color: white !important;
  border: none;
  padding: 4px 16px;
  border-radius: 16px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-size: 12px;
`;

export const StyledModal = styled(Modal)`
  .ant-modal {
    max-width: 100%;
    
    @media (min-width: 768px) {
      max-width: 600px;
    }
    
    @media (min-width: 1024px) {
      max-width: 800px;
    }
  }

  .ant-modal-content {
    background: ${(props) => getThemeValue(props, "#2D3748", "#ffffff")} !important;
    border-radius: 16px;
  }

  .ant-modal-header {
    background: ${(props) => getThemeValue(props, "#2D3748", "#ffffff")} !important;
    border-bottom: ${(props) => getThemeValue(props, "1px solid #4A5568", "1px solid #E2E8F0")};
    border-radius: 16px 16px 0 0;
  }

  .ant-modal-title {
    color: ${(props) => getThemeValue(props, "#ffffff", "#1A202C")} !important;
  }

  .ant-modal-body {
    color: ${(props) => getThemeValue(props, "#ffffff", "#1A202C")} !important;
  }

  .ant-modal-footer {
    border-top: ${(props) => getThemeValue(props, "1px solid #4A5568", "1px solid #E2E8F0")};
    background: ${(props) => getThemeValue(props, "#2D3748", "#ffffff")} !important;
  }
`;

export const ModalContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export const ModalHeaderSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const ModalCustomerInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const ModalCustomerName = styled(Text)`
  font-size: 20px;
  font-weight: 700;
`;

export const ModalDisputeId = styled(Text)`
  font-size: 12px;
  color: ${(props) => getThemeValue(props, "#CBD5E0", "#718096")} !important;
`;

export const ModalSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ModalSectionTitle = styled(Text)`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
  display: block;
`;

export const ModalSectionText = styled(Text)`
  font-size: 14px;
  color: ${(props) => getThemeValue(props, "#CBD5E0", "#718096")} !important;
`;

export const ModalCloseButton = styled(Button)`
  background: black;
  color: white;
  border: none;

  &:hover {
    color: black;
    background: white;
    border: 1px solid #d9d9d9;
  }
`;

export const SkeletonCard = styled(Card)`
  background: ${(props) => getThemeValue(props, "#2D3748", "#ffffff")} !important;
  border: ${(props) => getThemeValue(props, "1px solid #4A5568", "1px solid #E2E8F0")};
  border-radius: 16px;
  height: 320px;

  @media (max-width: 768px) {
    height: 280px;
  }

  .ant-card-body {
    padding: 24px;
  }
`;

export const EmptyState = styled.div`
  padding: 32px;
  text-align: center;
`;

export const EmptyStateText = styled(Text)`
  color: ${(props) => getThemeValue(props, "#CBD5E0", "#718096")} !important;
  font-size: 14px;
`;

export const FullWidthSpace = styled(Space)`
  width: 100%;
`;

export const ModalFooterSpace = styled(Space)`
  width: 100%;
  justify-content: flex-end;
  margin-top: 24px;
`;
